
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Button from '../../components/base/Button';

export default function About() {
  const { t } = useTranslation();

  useEffect(() => {
    // Update page title and meta description
    document.title = 'About WF & Y Painting LLC - Atlanta Painting Company Since 2009';
    
    // Add meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Learn about WF & Y Painting LLC, Atlanta\'s trusted painting company since 2009. 15+ years experience, 500+ projects completed, licensed & insured. Meet our team.');

    // Add structured data for about page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/about#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/about`,
      "name": "About WF & Y Painting LLC - Atlanta Painting Company",
      "description": "Learn about WF & Y Painting LLC, Atlanta's trusted painting company since 2009. 15+ years experience, 500+ projects completed, licensed & insured.",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${import.meta.env.VITE_SITE_URL}/#website`
      },
      "mainEntity": {
        "@type": "LocalBusiness",
        "@id": `${import.meta.env.VITE_SITE_URL}/#organization`
      }
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const values = [
    {
      icon: 'ri-award-fill',
      title: 'Quality Workmanship',
      description: 'We take pride in delivering exceptional results using premium materials and proven techniques.'
    },
    {
      icon: 'ri-time-fill',
      title: 'Reliable Service',
      description: 'On-time completion and clear communication throughout every project.'
    },
    {
      icon: 'ri-customer-service-2-fill',
      title: 'Customer Satisfaction',
      description: 'Your satisfaction is our priority. We work closely with you to exceed expectations.'
    },
    {
      icon: 'ri-shield-check-fill',
      title: 'Licensed & Insured',
      description: 'Fully licensed and insured for your peace of mind and protection.'
    }
  ];

  const team = [
    {
      name: 'William Rodriguez',
      role: 'Founder & Lead Painter',
      image: 'https://public.readdy.ai/ai/img_res/36cf2d26fbcc62cb37e22a3a7f687e77.jpg',
      description: 'With over 15 years of experience, William founded WF & Y Painting to provide Atlanta with exceptional painting services.'
    },
    {
      name: 'Carlos Martinez',
      role: 'Project Manager',
      image: 'https://readdy.ai/api/search-image?query=Professional%20project%20manager%20portrait%2C%20construction%20supervisor%2C%20friendly%20demeanor%2C%20work%20attire%2C%20clipboard%20and%20tools%2C%20experienced%20contractor%2C%20Hispanic%20male&width=300&height=400&seq=team2&orientation=portrait',
      description: 'Carlos ensures every project runs smoothly from start to finish, coordinating all aspects of our painting services.'
    },
    {
      name: 'Maria Gonzalez',
      role: 'Color Consultant',
      image: 'https://readdy.ai/api/search-image?query=Professional%20color%20consultant%20portrait%2C%20interior%20designer%2C%20paint%20samples%2C%20friendly%20smile%2C%20professional%20attire%2C%20Hispanic%20female%20designer&width=300&height=400&seq=team3&orientation=portrait',
      description: 'Maria helps clients choose the perfect colors and finishes to transform their spaces beautifully.'
    }
  ];

  return (
    <div className="min-h-screen pt-8">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                {t('aboutTitle')}
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                {t('aboutDescription')}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">15+</div>
                  <div className="text-blue-200">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold mb-2">500+</div>
                  <div className="text-blue-200">Projects Completed</div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20painting%20company%20team%20at%20work%2C%20experienced%20painters%20with%20equipment%2C%20quality%20craftsmanship%2C%20Atlanta%20painting%20crew%2C%20skilled%20workers%20on%20job%20site&width=600&height=500&seq=about-hero&orientation=landscape"
                alt="WF & Y Painting Team"
                className="rounded-xl shadow-2xl w-full h-96 object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-8">
              Our Story
            </h2>
            <div className="text-lg text-gray-600 space-y-6">
              <p>
                Founded in 2009, WF & Y Painting LLC began as a small family business with a simple mission: 
                to provide Atlanta homeowners and businesses with exceptional painting services they can trust. 
                What started as a one-person operation has grown into a full-service painting company serving 
                the greater Atlanta area.
              </p>
              <p>
                Our commitment to quality workmanship, reliable service, and customer satisfaction has earned 
                us a reputation as one of Atlanta's most trusted painting contractors. We take pride in using 
                only premium Sherwin Williams paints and materials, ensuring every project delivers lasting 
                beauty and protection.
              </p>
              <p>
                Today, we continue to grow while maintaining the personal touch and attention to detail that 
                our customers have come to expect. Every project, whether residential or commercial, receives 
                the same level of care and professionalism that has made us Atlanta's preferred painting company.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Our Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              These core values guide everything we do and ensure exceptional results on every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow duration-300">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${value.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Meet Our Team
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our experienced professionals are dedicated to delivering exceptional results on every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-80 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-4">
                    {member.role}
                  </p>
                  <p className="text-gray-600">
                    {member.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Certifications & Partnerships
            </h2>
            <p className="text-lg text-gray-600">
              We maintain the highest standards through professional certifications and quality partnerships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 text-center shadow-lg">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-award-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Sherwin Williams Partner
              </h3>
              <p className="text-gray-600">
                Certified partner using premium Sherwin Williams paints and materials for superior results.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center shadow-lg">
              <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Licensed & Insured
              </h3>
              <p className="text-gray-600">
                Fully licensed and insured for your protection and peace of mind on every project.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 text-center shadow-lg">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-fill text-white text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                BBB Accredited
              </h3>
              <p className="text-gray-600">
                Better Business Bureau accredited with an A+ rating for exceptional customer service.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            Ready to Work With Us?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Experience the WF & Y Painting difference on your next project
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button variant="secondary" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
                <i className="ri-mail-fill"></i>
                {t('getQuote')}
              </Button>
            </Link>
            <a href="tel:770-875-6990">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
                <i className="ri-phone-fill"></i>
                770-875-6990
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
